import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import styled from 'styled-components';
import { 
  EyeIcon, 
  EyeSlashIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  LockIcon 
} from '../../utils/IconWrapper';
import { registrationPhase1Schema, registerPhase1 } from '../../utils/supabaseExtended';
import { z } from 'zod';

const StepContainer = styled.div`
  padding: 1rem 0;
`;

const StepTitle = styled.h2`
  font-size: 1.5rem;
  color: #004744;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StepDescription = styled.p`
  color: var(--text-secondary);
  text-align: center;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
  max-width: 500px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  padding-left: 2.5rem;
  border: 2px solid ${props => props.hasError ? '#ef4444' : 'var(--border-color)'};
  border-radius: 8px;
  font-size: 1rem;
  background-color: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ef4444' : '#004744'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(0, 71, 68, 0.1)'};
  }
  
  &::placeholder {
    color: var(--text-secondary);
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  pointer-events: none;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const Checkbox = styled.input`
  margin-top: 0.25rem;
  cursor: pointer;
`;

const CheckboxLabel = styled.label<{ hasError?: boolean }>`
  font-size: 0.9rem;
  color: ${props => props.hasError ? '#ef4444' : 'var(--text-primary)'};
  cursor: pointer;
  line-height: 1.4;
  
  a {
    color: #004744;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SubmitButton = styled.button<{ loading?: boolean }>`
  background: linear-gradient(135deg, #004744 0%, #004744 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 71, 68, 0.4);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const BenefitsList = styled.div`
  background: var(--bg-primary);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  border-left: 4px solid #004744;
  border: 1px solid var(--border-color);
`;

const BenefitsTitle = styled.h4`
  color: #004744;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

type FormData = z.infer<typeof registrationPhase1Schema>;

interface RegistrationStep1Props {
  onNext: (data: FormData) => void;
  initialData?: Partial<FormData>;
}

const RegistrationStep1: React.FC<RegistrationStep1Props> = ({ 
  onNext, 
  initialData 
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm<FormData>({
    resolver: zodResolver(registrationPhase1Schema),
    defaultValues: initialData
  });

  const onSubmit = async (data: FormData) => {
    // Prevenzione click multipli - debounce di 3 secondi
    const now = Date.now();
    if (now - lastSubmitTime < 3000) {
      setSubmitError('Attendi qualche secondo prima di riprovare.');
      return;
    }
    setLastSubmitTime(now);

    setIsLoading(true);
    setSubmitError(null);

    try {
      console.log('🚀 Invio dati registrazione:', data);
      const result = await registerPhase1(data);
      
      console.log('📋 Risultato registrazione fase 1:', result);

      if (result.success) {
        console.log('✅ Registrazione fase 1 completata con successo');
        // Passa alla fase 2
        onNext(data);
      } else {
        console.error('❌ Errore registrazione fase 1:', result.error);
        
        // Gestione specifica per rate limit
        if (result.error?.includes('rate limit') || result.error?.includes('tentativi')) {
          const waitTime = 60; // 60 minuti
          const retryTime = new Date(Date.now() + waitTime * 60 * 1000);
          
          setSubmitError(
            `🚫 ${result.error}\n\n` +
            `⏰ Riprova dopo le ${retryTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}\n\n` +
            `💡 Alternative immediate:\n` +
            `• Se hai già un account: vai al Login\n` +
            `• Cambia email se ne hai un'altra\n` +
            `• Contattaci per assistenza immediata`
          );
        } else {
          setSubmitError(result.error || 'Errore durante la registrazione');
        }
      }
    } catch (error) {
      console.error('💥 Errore imprevisto registrazione:', error);
      
      // Gestione specifica anche per errori catturati
      const errorMessage = error instanceof Error ? error.message : 'Errore sconosciuto';
      if (errorMessage.includes('rate limit')) {
        setSubmitError('Troppi tentativi di registrazione. Attendi qualche minuto prima di riprovare.');
      } else {
        setSubmitError('Errore di connessione. Riprova più tardi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StepContainer>
      <StepTitle>Crea il tuo account</StepTitle>
      <StepDescription>
        Inizia il tuo viaggio con J-O-H. Bastano pochi dati per iniziare!
      </StepDescription>

      <BenefitsList>
        <BenefitsTitle>
          🎉 Cosa ottieni registrandoti:
        </BenefitsTitle>
        <BenefitItem>
          ✨ <strong>Sconti esclusivi</strong> fino al 20% sui nostri alloggi
        </BenefitItem>
        <BenefitItem>
          🏠 <strong>Accesso prioritario</strong> alle nuove proprietà
        </BenefitItem>
        <BenefitItem>
          📧 <strong>Offerte personalizzate</strong> via email
        </BenefitItem>
      </BenefitsList>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label>
            <EnvelopeIcon />
            Email
          </Label>
          <InputWrapper>
            <InputIcon>
              <EnvelopeIcon />
            </InputIcon>
            <Input
              type="email"
              placeholder="la-tua-email@esempio.com"
              hasError={!!errors.email}
              {...register('email')}
            />
          </InputWrapper>
          {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>
            <PhoneIcon />
            Numero di telefono
          </Label>
          <InputWrapper>
            <InputIcon>
              <PhoneIcon />
            </InputIcon>
            <Input
              type="tel"
              placeholder="+39 123 456 7890"
              hasError={!!errors.phone}
              {...register('phone')}
            />
          </InputWrapper>
          {errors.phone && <ErrorMessage>{errors.phone.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>
            <LockIcon />
            Password
          </Label>
          <InputWrapper>
            <InputIcon>
              <LockIcon />
            </InputIcon>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Crea una password sicura"
              hasError={!!errors.password}
              {...register('password')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </PasswordToggle>
          </InputWrapper>
          {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>
            <LockIcon />
            Conferma password
          </Label>
          <InputWrapper>
            <InputIcon>
              <LockIcon />
            </InputIcon>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Ripeti la password"
              hasError={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </PasswordToggle>
          </InputWrapper>
          {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
        </FormGroup>

        <CheckboxGroup>
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id="terms"
              {...register('termsAccepted')}
            />
            <CheckboxLabel htmlFor="terms" hasError={!!errors.termsAccepted}>
              Accetto i <a href="/terms-of-service" target="_blank">Termini e Condizioni</a> di J-O-H
            </CheckboxLabel>
          </CheckboxWrapper>
          {errors.termsAccepted && <ErrorMessage>{errors.termsAccepted.message}</ErrorMessage>}

          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id="privacy"
              {...register('privacyAccepted')}
            />
            <CheckboxLabel htmlFor="privacy" hasError={!!errors.privacyAccepted}>
              Accetto la <a href="/privacy-policy" target="_blank">Privacy Policy</a>
            </CheckboxLabel>
          </CheckboxWrapper>
          {errors.privacyAccepted && <ErrorMessage>{errors.privacyAccepted.message}</ErrorMessage>}

          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              id="newsletter"
              defaultChecked
              {...register('newsletterSubscribed')}
            />
            <CheckboxLabel htmlFor="newsletter">
              Voglio ricevere <strong>sconti esclusivi</strong> e aggiornamenti sui <strong>nuovi immobili</strong> via email
            </CheckboxLabel>
          </CheckboxWrapper>
        </CheckboxGroup>

        {submitError && (
          <ErrorMessage style={{ textAlign: 'center', fontSize: '1rem' }}>
            {submitError}
          </ErrorMessage>
        )}

        <SubmitButton type="submit" disabled={isLoading} loading={isLoading}>
          {isLoading && <LoadingSpinner />}
          Crea Account e Continua
        </SubmitButton>
      </Form>
    </StepContainer>
  );
};

export default RegistrationStep1; 